import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { OpenTimesheetApprovalTableRow } from './useAllOPLogsMapped';
import { Typography } from '../../../components/Typography/Typography';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Box, Button, Chip, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { SelectItem } from '../../../types';
import { roleLabel } from '../../../utils/roleLabels';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  approvalDetailsModalState,
  tsApprovalsSelectedOYearFilterState,
} from '../../../state/UIAccountantState';
import { ROUTES } from '../../../constants/routes';
import { useLocation, useNavigate } from 'react-router';

const columnHelper = createColumnHelper<OpenTimesheetApprovalTableRow>();

export const useAllOpenTSTableColumns = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const setApprovalDetailsModal = useSetAtom(approvalDetailsModalState);
  const selectedYear = useAtomValue(tsApprovalsSelectedOYearFilterState);

  const getChipColor = useCallback(
    (status: 'On track' | 'Needs Attention' | 'Critical') => {
      if (status === 'On track') return colors.textStatus.success;
      else if (status === 'Needs Attention') return colors.textStatus.warning;
      return colors.textStatus.error;
    },
    [colors.textStatus.success, colors.textStatus.warning, colors.textStatus.error]
  );

  const handleApprovalDetailsModal = useCallback(
    (userId: number, tsId: number) => {
      setApprovalDetailsModal({
        userId,
        tsId,
      });
    },
    [setApprovalDetailsModal]
  );

  const redirectToSingleTs = useCallback(
    (tsId: number) => {
      navigate(`/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}/${tsId}?year=${selectedYear}`, {
        state: { from: location.pathname.slice(1) },
      });
    },
    [navigate, selectedYear, location.pathname]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('timesheetName', {
        cell: (info) => (
          <Button
            variant='text'
            onClick={() => redirectToSingleTs(info.row.original.tsId)}
            style={{
              fontSize: '16px',
            }}
          >
            {info.getValue()}
          </Button>
        ),
        header: () => <TableHeaderCell text='Timesheet Name' />,
        meta: {
          width: '330px',
          maxWidth: '330px',
          minWidth: '330px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((user) => row.original.timesheetName.includes(user.value));
        },
      }),
      columnHelper.accessor('status', {
        cell: (info) => (
          <Chip
            style={{
              backgroundColor: colors.surfaceStatus.noAction,
              minWidth: '66px',
              height: '28px',
            }}
            label={
              <Typography variant='body' color={getChipColor(info.getValue())}>
                {info.getValue()}
              </Typography>
            }
          />
        ),
        header: () => <TableHeaderCell text='Status' />,
        meta: {
          width: '160px',
          maxWidth: '160px',
          minWidth: '160px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((status) => row.original.status === status.value);
        },
      }),
      columnHelper.accessor('submittedOn', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main}>
            {info.getValue() ? format(parseISO(info.getValue() ?? ''), 'MMM d, yyyy') : '-'}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Submitted On' />,
        meta: {
          width: '130px',
          maxWidth: '130px',
          minWidth: '130px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('duration', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main} style={{ textAlign: 'right' }}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Duration (Days)' />,
        meta: {
          width: '140px',
          maxWidth: '140px',
          minWidth: '140px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('cycle', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main} style={{ textAlign: 'right' }}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Cycle' />,
        meta: {
          width: '66px',
          maxWidth: '66px',
          minWidth: '66px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('pendingWith', {
        cell: (info) => (
          <Box display='flex' alignItems='center' gap='8px'>
            <Typography variant='body' color={colors.text.main}>
              {info.getValue()?.users.join(', ')}
            </Typography>
            <Chip
              style={{
                backgroundColor: colors.surfaceStatus.noAction,
                minWidth: '66px',
                height: '28px',
              }}
              label={
                <Typography variant='body' color={colors.text.secondary}>
                  {roleLabel(info.getValue()?.role)} {info.getValue()?.details}
                </Typography>
              }
            />
          </Box>
        ),
        header: () => <TableHeaderCell text='Pending With (Name | Role)' />,
        meta: {
          minWidth: '380px',
          maxWidth: '380px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((selectedUser) => {
            // Role-level (parent) selections carry the raw role in `id`; the display `value` is now the
            // relabelled role (e.g. "Submitter"), so match the role on `id`. Child selections match by user.
            if (row.original.pendingWith?.role === selectedUser.id) {
              return true;
            }
            return row.original.pendingWith?.users?.includes(selectedUser.value);
          });
        },
      }),
      columnHelper.accessor('pendingDays', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.main} style={{ textAlign: 'right' }}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Pending (Days)' />,
        meta: {
          width: '140px',
          maxWidth: '140px',
          minWidth: '140px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <Button
            variant='outlined'
            onClick={() => {
              handleApprovalDetailsModal(info.row.original.userId, info.row.original.tsId);
            }}
            style={{
              backgroundColor: 'transparent',
              height: '28px',
            }}
          >
            <Typography
              variant='body'
              color={colors.textAccent.default}
              style={{ marginBottom: '-1px' }}
            >
              Review
            </Typography>
          </Button>
        ),
        header: () => <TableHeaderCell text='Action' />,
        meta: {
          width: '115px',
          maxWidth: '115px',
          minWidth: '115px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
    ],
    [
      colors.surfaceStatus.noAction,
      colors.text.main,
      colors.text.secondary,
      colors.textAccent.default,
      getChipColor,
      handleApprovalDetailsModal,
      redirectToSingleTs,
    ]
  );
  return columns;
};
